import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  FileText, 
  Globe, 
  Share2, 
  CheckSquare,
  Settings,
  Star,
  Mail,
  FormInput,
  LogOut,
  Database,
  Mic,
  Sliders
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Overview', icon: BarChart3 },
    { path: '/dashboard/crm', label: 'CRM', icon: Users, subItems: [
      { path: '/dashboard/crm/settings', label: 'CRM Settings', icon: Settings },
      { path: '/dashboard/crm/integrations', label: 'CRM Integrations', icon: Database }
    ]},
    { path: '/dashboard/tasks', label: 'Tasks', icon: CheckSquare },
    { path: '/dashboard/lead-capture', label: 'Lead Capture System', icon: FormInput },
    { path: '/dashboard/landing-pages', label: 'Landing Pages', icon: Globe },
    { path: '/dashboard/social-hub', label: 'Social Hub', icon: Share2 },
    { path: '/dashboard/email-marketing', label: 'Email Marketing', icon: Mail },
    { path: '/dashboard/reviews', label: 'Reviews', icon: Star },
    { path: '/dashboard/voice-agents', label: 'AI Voice Agents', icon: Mic },
    { path: '/dashboard/risk-number-prospects', label: 'Risk Number Prospects', icon: Sliders },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings }
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`bg-white shadow-lg flex flex-col transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
        <div className={`p-6 border-b border-gray-200 flex ${collapsed ? 'justify-center' : 'justify-between'} items-center`}>
          {!collapsed && <h2 className="text-2xl font-semibold text-gray-800">Dashboard</h2>}
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-500 hover:text-gray-700"
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                            (item.subItems && item.subItems.some(subItem => location.pathname === subItem.path));
            
            return (
              <div key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center ${collapsed ? 'justify-center' : 'px-6'} py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-primary bg-primary/5'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
                  {!collapsed && item.label}
                </Link>
                
                {/* Sub-items */}
                {!collapsed && item.subItems && item.subItems.map(subItem => {
                  const SubIcon = subItem.icon;
                  const isSubActive = location.pathname === subItem.path;
                  
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className={`flex items-center pl-14 pr-6 py-2 text-sm font-medium transition-colors ${
                        isSubActive
                          ? 'text-primary bg-primary/5'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <SubIcon className="w-4 h-4 mr-3" />
                      {subItem.label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleSignOut}
            className={`flex items-center ${collapsed ? 'justify-center' : 'px-6'} py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 w-full rounded-md`}
          >
            <LogOut className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;